import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWallet, startCheckout } from '../services/billingService';
import './WalletBadge.css';

/**
 * Displays the user's credit balance and a top-up button.
 * Renders nothing if the wallet cannot be loaded (e.g. user not yet logged in).
 */
export default function WalletBadge() {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);

  const loadWallet = useCallback(async () => {
    try {
      const data = await fetchWallet();
      setWallet(data);
    } catch {
      // Not logged in yet or profile missing — hide the badge silently
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWallet();
    // Refresh wallet every 60 seconds
    const interval = setInterval(loadWallet, 60000);
    return () => clearInterval(interval);
  }, [loadWallet]);

  const handleBuyCredits = async () => {
    setBuying(true);
    try {
      await startCheckout('launch_pack');
    } catch (err) {
      console.error('Checkout error:', err);
    } finally {
      setBuying(false);
    }
  };

  if (loading || !wallet) return null;

  const isLow = wallet.availableCredits <= 10;
  const isEmpty = wallet.availableCredits === 0;

  return (
    <div
      className={`wallet-badge ${isLow ? 'wallet-badge--low' : ''}`}
      onClick={() => navigate('/profile')}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && navigate('/profile')}
      title="Go to profile settings"
    >
      <span className="wallet-badge__icon">◈</span>
      <span className="wallet-badge__credits">
        {wallet.availableCredits} credit{wallet.availableCredits !== 1 ? 's' : ''}
      </span>
      {isEmpty && (
        <button
          className="wallet-badge__cta"
          onClick={(e) => { e.stopPropagation(); handleBuyCredits(); }}
          disabled={buying}
        >
          {buying ? 'Loading…' : 'Buy credits'}
        </button>
      )}
    </div>
  );
}
